import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AdyenDropinWrapper } from './adyen-dropin-wrapper';
import { Button } from '@/components/button';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { PadAdditionalTerms } from '@/components/payments/pad-additional-terms';
import { PadAgreement } from '@/components/payments/pad-agreement';
import { PadContactInformationInputs } from '@/components/payments/pad-contact-information-inputs';
import { PadPaymentInfoInputs } from './pad-payment-info-inputs';
import {
  PaymentMethodOption,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { PaymentMethodSelector } from './payment-method-selector';
import { SCREEN_PADDING } from '@/styles';
import { TabCardApplicationContent } from '@/components/payments/tab-card-application-content';
import { TabCardDetails } from '@/components/payments/types';
import { useInitiatePlaid } from '@/hooks/use-initiate-plaid';
import { useLayoutContext } from '@/contexts/layout-context';
import { usePadPaymentFlow } from '@/hooks/payments/use-pad-payment-operations';
import { useToasts } from '@/contexts/toast-context';
import { useWindowDimensions, View } from 'react-native';

enum WalletActionSheetView {
  AddPADAdditionalTerms = 'ADD_PAD_ADDITIONAL_TERMS',
  AddPADAgreement = 'ADD_PAD_AGREEMENT',
  AddPADForm = 'ADD_PAD_FORM',
  AddPaymentMethod = 'ADD_PAYMENT_METHOD',
  Loading = 'LOADING',
  TabCardApplication = 'TAB_CARD_APPLICATION',
  ViewPaymentMethods = 'VIEW_PAYMENT_METHODS',
}

export interface Props {
  footerBanner?: (navigation: {
    jumpToTabCardApplication: () => void;
  }) => React.ReactNode;
  onClose: () => void;
  onSavePaymentMethod?: () => void;
  onSelectPaymentMethod?: (paymentMethod?: PaymentMethodOption) => void;
  tabCardDetails?: TabCardDetails | null;
}

export const WalletActionSheet: React.FC<Props> = ({
  footerBanner,
  onClose,
  onSavePaymentMethod,
  onSelectPaymentMethod,
  tabCardDetails,
}) => {
  const { plaidEnabled, refetchPaymentContext, transactionFeeMap } =
    usePaymentsContext();
  const { showErrorToast } = useToasts();
  const initiatePlaid = useInitiatePlaid();
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const { height: windowHeight } = useWindowDimensions();

  const paymentMethodListStyle = isSmallLayout
    ? { height: Math.round(windowHeight * 0.8) }
    : null;

  const {
    formState,
    dispatch,
    isSubmittingPad,
    isLoadingConsentForms,
    consentFormsData,
    fetchConsentAgreementText,
    handleSubmitPad,
    areAllFieldsValid,
  } = usePadPaymentFlow();

  return (
    <CombinedActionSheet
      initialRoute={WalletActionSheetView.ViewPaymentMethods}
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case WalletActionSheetView.AddPADForm: {
            return (
              <View>
                <View>
                  <PadPaymentInfoInputs
                    dispatch={dispatch}
                    formState={formState}
                  />
                  <View style={styles.padFormSpacer} />
                  <PadContactInformationInputs
                    dispatch={dispatch}
                    formState={formState}
                  />
                </View>
                <View style={styles.padButtonContainer}>
                  <Button
                    disabled={!areAllFieldsValid}
                    onPress={async () => {
                      jumpTo(WalletActionSheetView.AddPADAgreement);
                      await fetchConsentAgreementText();
                    }}
                    style={styles.padButton}
                    trackingData={{ name: 'pad_desktop_form.save_button' }}
                  >
                    {i18n.t('save')}
                  </Button>
                </View>
              </View>
            );
          }
          case WalletActionSheetView.AddPADAgreement: {
            if (isLoadingConsentForms || !consentFormsData) {
              return (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator />
                </View>
              );
            }
            return (
              <View>
                <View style={styles.padAgreementBoxBorder}>
                  <PadAgreement
                    markdownTemplate={consentFormsData.padAgreement}
                    onViewAdditionalTerms={() =>
                      jumpTo(WalletActionSheetView.AddPADAdditionalTerms)
                    }
                  />
                </View>
                <View style={styles.padButtonContainer}>
                  <Button
                    disabled={isSubmittingPad}
                    loading={isSubmittingPad}
                    onPress={() =>
                      handleSubmitPad(() =>
                        jumpTo(WalletActionSheetView.ViewPaymentMethods),
                      )
                    }
                    style={styles.padButton}
                    trackingData={{ name: 'pad_agreement.agree_button' }}
                  >
                    {i18n.t('agree')}
                  </Button>
                </View>
              </View>
            );
          }
          case WalletActionSheetView.AddPADAdditionalTerms: {
            if (!consentFormsData) {
              return (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator />
                </View>
              );
            }
            return (
              <View style={styles.padAgreementBoxBorder}>
                <PadAdditionalTerms
                  markdownTemplate={consentFormsData.padAdditionalTerms}
                />
              </View>
            );
          }
          case WalletActionSheetView.AddPaymentMethod: {
            return (
              <AdyenDropinWrapper
                onSaveSuccess={
                  onSavePaymentMethod
                    ? onSavePaymentMethod
                    : () => jumpTo(WalletActionSheetView.ViewPaymentMethods)
                }
                requireSavedPaymentMethod
                transactionFeeMap={transactionFeeMap}
              />
            );
          }
          case WalletActionSheetView.Loading: {
            return (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" />
              </View>
            );
          }
          case WalletActionSheetView.TabCardApplication: {
            return tabCardDetails ? (
              <TabCardApplicationContent
                onAfterSignUp={onClose}
                tabCardDetails={tabCardDetails}
              />
            ) : null;
          }
          case WalletActionSheetView.ViewPaymentMethods: {
            return (
              <View
                style={[styles.paymentMethodSelector, paymentMethodListStyle]}
              >
                <PaymentMethodSelector
                  footerBanner={footerBanner?.({
                    jumpToTabCardApplication: () =>
                      jumpTo(WalletActionSheetView.TabCardApplication),
                  })}
                  onAddMethod={() =>
                    jumpTo(WalletActionSheetView.AddPaymentMethod)
                  }
                  onAddPAD={() => {
                    jumpTo(WalletActionSheetView.AddPADForm);
                  }}
                  onConnectBankAccount={
                    plaidEnabled
                      ? async () => {
                          try {
                            jumpTo(WalletActionSheetView.Loading);
                            await initiatePlaid();

                            // Once the browser is closed, refresh the payment
                            // methods in case a new one was added for Plaid
                            await refetchPaymentContext();
                            jumpTo(WalletActionSheetView.ViewPaymentMethods);
                          } catch (e) {
                            showErrorToast();
                            jumpTo(WalletActionSheetView.ViewPaymentMethods);
                          }
                        }
                      : undefined
                  }
                  onSelectMethod={
                    onSelectPaymentMethod
                      ? (paymentMethod?: PaymentMethodOption) => {
                          onSelectPaymentMethod(paymentMethod);
                        }
                      : undefined
                  }
                />
              </View>
            );
          }
          default: {
            return null;
          }
        }
      }}
      routes={{
        [WalletActionSheetView.AddPADForm]: {
          previousKey: WalletActionSheetView.ViewPaymentMethods,
          title: i18n.t('add_pad_ach'),
        },
        [WalletActionSheetView.AddPADAgreement]: {
          previousKey: WalletActionSheetView.AddPADForm,
          title: i18n.t('add_pad_ach'),
        },
        [WalletActionSheetView.AddPADAdditionalTerms]: {
          previousKey: WalletActionSheetView.AddPADAgreement,
          title: i18n.t('add_pad_ach'),
        },
        [WalletActionSheetView.AddPaymentMethod]: {
          previousKey: WalletActionSheetView.ViewPaymentMethods,
          title: i18n.t('add_payment_method'),
        },
        [WalletActionSheetView.Loading]: {
          previousKey: null,
        },
        [WalletActionSheetView.TabCardApplication]: {
          previousKey: WalletActionSheetView.ViewPaymentMethods,
          title: i18n.t('tab_card_modal.header'),
        },
        [WalletActionSheetView.ViewPaymentMethods]: {
          previousKey: null,
          title: i18n.t('wallet'),
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  loadingContainer: {
    paddingBottom: theme.sk.spacing.space3xlarge,
    paddingTop: theme.sk.spacing.spaceXlarge,
  },
  padAgreementBoxBorder: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
  },
  padButton: {
    alignSelf: 'flex-end',
    borderRadius: theme.sk.radii.radiusMedium,
    minWidth: 120,
  },
  padButtonContainer: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  padFormSpacer: {
    height: theme.sk.spacing.spaceLarge,
  },
  paymentMethodSelector: {
    // Reduces inherited spacing from `BaseActionSheet`
    marginHorizontal: -SCREEN_PADDING,
  },
}));
